/**
 * Top Navigation Component
 * Minimal header: brand, menu, role switcher, profile avatar (game & group moved to LeftNav).
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useGame } from '../contexts/GameContext';
import { usePlan } from '../contexts/PlanContext';
import { getPlanById } from '../constants/plans';
import RoleSwitcher from './RoleSwitcher';
import CachedAvatar from './CachedAvatar';
import './TopNav.css';

export default function TopNav({ title, onMenuClick, menuOpen, menuAriaLabel = 'Open menu' }) {
  const { currentUser, userProfile, logout } = useAuth();
  const { isImpersonating, effectiveProfile, stopImpersonating } = useImpersonation();
  const { currentGame } = useGame();
  const { planId } = usePlan();
  const navigate = useNavigate();
  const plan = getPlanById(planId);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const profile = isImpersonating ? effectiveProfile : userProfile;
  const photoURL = profile?.photoURL || profile?.avatarURL || currentUser?.photoURL;
  const displayName = profile?.displayName || currentUser?.displayName || currentUser?.email || profile?.email || '';

  return (
    <header className="top-nav">
      {isImpersonating && (
        <div className="top-nav-login-as-banner" role="status" aria-live="polite">
          <span className="top-nav-login-as-label">Login as</span>
          <span className="top-nav-login-as-name">{displayName || 'Unknown'}</span>
          <button
            type="button"
            className="top-nav-login-as-stop"
            onClick={stopImpersonating}
            aria-label="Stop Login as and return to your account"
          >
            Stop
          </button>
        </div>
      )}
      <div className="top-nav-content">
        <h1
          className="top-nav-title"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
          aria-label="GradedPlay - Home"
        >
          <span className="top-nav-brand">
            <img src="/design/logos/gradedplay-high-resolution-logo-tranparent.png" alt="" className="top-nav-logo-img" aria-hidden />
          </span>
        </h1>
        <div className="top-nav-actions">
          {onMenuClick && (
            <button
              type="button"
              className="top-nav-menu-trigger"
              onClick={onMenuClick}
              aria-expanded={menuOpen}
              aria-label={menuAriaLabel}
            >
              <span className="top-nav-menu-icon" aria-hidden>{menuOpen ? '✕' : '☰'}</span>
            </button>
          )}
          <RoleSwitcher />
          <button
            type="button"
            className="top-nav-plan-badge"
            onClick={() => navigate('/subscription')}
            title={`Your plan: ${plan.name}. Click to view plans.`}
            aria-label={`Current plan: ${plan.name}. Go to plans.`}
          >
            <span className="top-nav-plan-badge-text">{plan.name}</span>
          </button>
          <div className="top-nav-user-dropdown" ref={userMenuRef}>
            <button
              type="button"
              className="top-nav-user-trigger top-nav-user-trigger-avatar"
              onClick={() => setUserMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              aria-label="Profile / user menu"
              title={displayName || 'Profile'}
            >
              <span className="top-nav-user-avatar-wrap">
                <span className="top-nav-user-avatar-fallback" aria-hidden>
                  {displayName ? displayName.trim().charAt(0).toUpperCase() : '?'}
                </span>
                {photoURL && (
                  <CachedAvatar
                    url={photoURL}
                    name={displayName}
                    className="top-nav-user-avatar"
                    alt=""
                  />
                )}
              </span>
              <span className="top-nav-user-chevron" aria-hidden>{userMenuOpen ? '▲' : '▼'}</span>
            </button>
            {userMenuOpen && (
              <ul className="top-nav-user-menu" role="menu">
                <li role="none">
                  <button
                    type="button"
                    className="top-nav-user-option"
                    role="menuitem"
                    onClick={() => {
                      setUserMenuOpen(false);
                      const qs = new URLSearchParams(window.location.search);
                      qs.set('nocache', '1');
                      window.location.href = window.location.pathname + '?' + qs.toString();
                    }}
                    title="Load latest app version (clears cache)"
                  >
                    Refresh app
                  </button>
                </li>
                <li role="none">
                  <button type="button" className="top-nav-user-option" role="menuitem" onClick={() => { setUserMenuOpen(false); navigate('/profile/edit'); }}>
                    Edit profile
                  </button>
                </li>
                <li role="none">
                  <button type="button" className="top-nav-user-option top-nav-user-option-logout" role="menuitem" onClick={() => { setUserMenuOpen(false); logout(); }}>
                    Logout
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

